import type { PunchStateFields } from '@/data/punchState/models/punchState';
import { PunchStateSchema } from '@/data/punchState/schema';
import type { QueryString, TerminalAuth } from '@/data/types';
import Monolith from '@/shared/api/monolith';
import V3 from '@/shared/api/v3';
import { parseAPIError } from '@/shared/redux/helpers/parseApiError';
import { createWiwAsyncThunk } from '@/store';

type FetchPunchStateRequest = {
  deviceType?: 'web' | 'terminal';
  params?: QueryString;
  skipCache?: boolean;
} & TerminalAuth;

type FetchPunchStateArgs = FetchPunchStateRequest & {
  apiVersion?: 'v2' | 'v3';
};

export const fetchPunchState = ({
  deviceType = 'web',
  userId,
  token,
  params = {},
  apiVersion = 'v2',
  skipCache = false,
}: FetchPunchStateArgs) => {
  if (apiVersion === 'v3') {
    return fetchPunchStateV3({ deviceType, userId, token, params, skipCache });
  }
  return fetchPunchStateV2({ deviceType, userId, token, params, skipCache });
};

export const fetchPunchStateV2 = createWiwAsyncThunk<
  PunchStateFields,
  FetchPunchStateRequest,
  { fulfilledMeta: { userId: string | number | null } }
>(
  'punchState/fetch',
  async ({ deviceType, userId, token, params = {}, skipCache }, { rejectWithValue, fulfillWithValue, getState }) => {
    const reqUserId = userId ?? getState().auth.userId;
    params = { userId: reqUserId, deviceType, ...params };

    let headers: HeadersInit = {};
    if (!!userId && !!token) {
      headers = {
        'W-Token': token,
        'W-UserId': userId.toString(),
        Authorization: `Bearer ${token}`,
      };
    }
    try {
      const result = await Monolith.url('/punch/state')
        // Forced refreshes must bypass both the response cache and any stale in-flight request.
        .options({ skipCache, skipDedupe: !!skipCache })
        .query(params)
        .headers(headers)
        .get();
      const parsed = PunchStateSchema.parse(result);
      return fulfillWithValue(parsed, {
        userId: reqUserId,
      });
    } catch (error) {
      return rejectWithValue(parseAPIError(error));
    }
  },
);

export const fetchPunchStateV3 = createWiwAsyncThunk<
  PunchStateFields,
  FetchPunchStateRequest,
  { fulfilledMeta: { userId: string | number | null } }
>(
  'punchState/fetchV3',
  async (
    { deviceType = 'web', userId, token, params = {}, skipCache },
    { rejectWithValue, fulfillWithValue, getState },
  ) => {
    const reqUserId = userId ?? getState().auth.userId;
    params = { userId: reqUserId, deviceType, ...params };
    let headers: HeadersInit = {};
    if (!!userId && !!token) {
      headers = {
        'W-Token': token,
        'W-UserId': userId.toString(),
        Authorization: `Bearer ${token}`,
      };
    }
    try {
      const result = await V3.url('/punch/state')
        // Forced refreshes must bypass both the response cache and any stale in-flight request.
        .options({ skipCache, skipDedupe: !!skipCache })
        .query(params)
        .headers(headers)
        .get();
      const parsed = PunchStateSchema.parse(result);
      return fulfillWithValue(parsed, {
        userId: reqUserId,
      });
    } catch (error) {
      return rejectWithValue(parseAPIError(error));
    }
  },
);
